import type { ReactNode } from 'react';
import { cx } from '../cx';
import { AnnotationMarkers, type ChartAnnotation } from './Annotations';
import { devWarn } from '../dev';
import { WIDE_ONLY, type ChartLayout } from './layout';
import { TICK_TEXT, VALUE_TEXT, chartFill, niceScale, percentOf, sparseLabels } from './scale';

export type ColumnPeriod = {
  id: string;
  label: ReactNode;
  value: number;
  /** Formatted value, shown on the highlighted column. */ display: ReactNode;
};

export type ColumnChartProps = {
  /** In time order, oldest first. */
  periods: ColumnPeriod[];
  /** The period the headline is about (the latest, a record): sun with its value. Defaults to the last. */
  highlightId?: string;
  /** A reference line (average, median), dashed, with its label ("Average 718"). */
  reference?: { value: number; label: ReactNode };
  /** Formats the axis ticks for the page's language. */
  formatTick: (value: number) => string;
  /** Events marked on the chart, numbered in order; list them under it with AnnotationList. */
  annotations?: ChartAnnotation[];
  /** `auto` (default) switches to the narrow form in a narrow container: a shorter plot and period labels only at the first and last column. */
  layout?: ChartLayout;
  /** One sentence that states the finding and the key values, for screen readers. */
  description: string;
  className?: string;
};

const PLOT: Record<ChartLayout, string> = { auto: 'h-30 @md:h-40', narrow: 'h-30', wide: 'h-40' };

/** Columns for counts per period (weeks, months). Columns start at zero; every period is one column. */
export function ColumnChart({
  periods,
  highlightId,
  reference,
  formatTick,
  annotations,
  layout = 'auto',
  description,
  className,
}: ColumnChartProps) {
  devWarn(periods.length > 24, `ColumnChart has ${periods.length} periods; 24 at most (12 on phones).`);
  const top = Math.max(1, ...periods.map((p) => p.value), reference?.value ?? 0);
  const scale = niceScale(0, top, { zero: true });
  const highlight = highlightId ?? periods[periods.length - 1]?.id;
  const showLabel = sparseLabels(periods.length);
  return (
    <div role="img" aria-label={description} className={cx('@container flex flex-col gap-1', className)}>
      <div className="flex gap-2">
        <div className={cx('relative w-10 shrink-0', PLOT[layout])} aria-hidden="true">
          {scale.ticks.map((t) => (
            <span
              key={t}
              className={cx('absolute right-0 translate-y-1/2', TICK_TEXT)}
              style={{ bottom: `${percentOf(t, scale.min, scale.max)}%` }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
        <div className={cx('relative flex-1', PLOT[layout])}>
          {annotations?.length ? (
            <AnnotationMarkers annotations={annotations} positionOf={(i) => ((i + 0.5) / periods.length) * 100} />
          ) : null}
          {scale.ticks.map((t) => (
            <span
              key={t}
              className="absolute inset-x-0 border-t border-jc-chart-grid"
              style={{ bottom: `${percentOf(t, scale.min, scale.max)}%` }}
            />
          ))}
          <div className="absolute inset-0 flex items-end gap-1">
            {periods.map((p) => {
              const h = percentOf(p.value, scale.min, scale.max);
              const isHighlight = p.id === highlight;
              return (
                <div key={p.id} className="relative flex h-full flex-1 items-end">
                  <span
                    className={cx(
                      'block w-full origin-bottom rounded-t-jc-info motion-safe:animate-jc-grow-y',
                      chartFill(isHighlight ? 'highlight' : 'context'),
                    )}
                    style={{ height: `${h}%` }}
                  />
                  {isHighlight ? (
                    <span
                      className={cx('absolute left-1/2 -translate-x-1/2 -translate-y-1', VALUE_TEXT)}
                      style={{ bottom: `${h}%` }}
                    >
                      {p.display}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
          {reference ? (
            <span
              className="absolute inset-x-0 border-t-2 border-dashed border-jc-ink-secondary"
              style={{ bottom: `${percentOf(reference.value, scale.min, scale.max)}%` }}
            >
              <span className="absolute left-1 -translate-y-full pb-0.5 text-xs font-semibold text-jc-ink-secondary">
                {reference.label}
              </span>
            </span>
          ) : null}
        </div>
      </div>
      <div className="relative mr-0 ml-12 h-4" aria-hidden="true">
        {periods.map((p, i) =>
          showLabel[i] ? (
            <span
              key={p.id}
              className={cx(
                'absolute -translate-x-1/2 whitespace-nowrap',
                TICK_TEXT,
                i !== 0 && i !== periods.length - 1 && WIDE_ONLY[layout],
              )}
              style={{ left: `${((i + 0.5) / periods.length) * 100}%` }}
            >
              {p.label}
            </span>
          ) : null,
        )}
      </div>
    </div>
  );
}
